import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ActivityTimeline from '../../../common/components/ActivityTimeline/ActivityTimeline';
import apiClient from '../../../common/functions/api_axios_instance.function';
import { handleApiError } from '../../../common/functions/errorHandler.function';

const InvoiceHistoryModal = ({
  open,
  onClose,
  invoiceId,
  fetchAuditLogsFunction,
  auditLogs: propAuditLogs,
  isLoadingAuditLogs: propIsLoadingAuditLogs,
  onRefresh,
}) => {
  // Mock data for testing (remove when backend is ready)
  const getMockHistoryData = () => [
    {
      timestamp: new Date().toISOString(),
      user: 'John Doe',
      action: 'Invoice status changed from Pending to Approved',
    },
    {
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      user: 'Jane Smith',
      action: 'Invoice forwarded to Finance Team',
    },
    {
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      user: 'Mike Johnson',
      action: 'Invoice created',
    },
    {
      timestamp: new Date(Date.now() - 10800000).toISOString(),
      user: 'Sarah Williams',
      action: 'Invoice amount updated from $10,000 to $12,500',
    },
    {
      timestamp: new Date(Date.now() - 14400000).toISOString(),
      user: 'David Brown',
      action: 'Invoice document uploaded',
    },
    {
      timestamp: new Date(Date.now() - 18000000).toISOString(),
      user: 'Emily Davis',
      action: 'Invoice submitted for review',
    },
  ];

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);

  // Use prop audit logs if provided, otherwise use state (for backward compatibility)
  const displayAuditLogs = propAuditLogs !== undefined ? propAuditLogs : auditLogs;
  const displayLoadingAuditLogs =
    propIsLoadingAuditLogs !== undefined ? propIsLoadingAuditLogs : isLoadingAuditLogs;

  // Transform audit logs for ActivityTimeline component
  const transformAuditLogsForTimeline = (logs) => {
    return logs.map((log) => {
      // Get the role name, show "Vendor" if role is "USER"
      const roleName =
        log.performed_by_role || log.user || log.userName || log.username || 'Unknown';
      const displayName = roleName === 'USER' ? 'Vendor' : roleName;

      return {
        timeStamp: log.timestamp || log.created_at || log.date,
        username: displayName,
        summary: log.action || log.change || log.description || log.summary || 'No description',
        details: log.details || null,
      };
    });
  };

  // Fetch invoice history/audit logs from API
  const fetchInvoiceHistory = useCallback(async () => {
    if (!invoiceId) {
      return;
    }

    // If onRefresh callback is provided, use it to refresh data in parent component
    if (onRefresh) {
      try {
        await onRefresh();
        return;
      } catch (error) {
        handleApiError(error);
        return;
      }
    }

    if (propAuditLogs !== undefined && fetchAuditLogsFunction) {
      try {
        await fetchAuditLogsFunction(invoiceId);
        return;
      } catch (error) {
        handleApiError(error);
        return;
      }
    }

    // If audit logs are provided as props but no fetch function, skip
    if (propAuditLogs !== undefined) {
      return;
    }

    // Otherwise, fetch logs internally (for backward compatibility)
    try {
      setIsLoadingAuditLogs(true);

      // Use custom fetch function if provided, otherwise use default behavior
      if (fetchAuditLogsFunction) {
        const response = await fetchAuditLogsFunction(invoiceId);
        // Handle different response structures
        const logs = Array.isArray(response)
          ? response
          : response?.data && Array.isArray(response.data)
            ? response.data
            : response?.response && Array.isArray(response.response)
              ? response.response
              : [];
        setAuditLogs(logs);
      } else {
        // Default behavior: use mock data for backward compatibility
        // TODO: Uncomment when backend API is ready
        // const response = await apiClient.get(`/vendor/invoice/${invoiceId}/history`);
        // if (response?.data && Array.isArray(response.data)) {
        //   setAuditLogs(response.data);
        // } else {
        //   setAuditLogs([]);
        // }

        // For now, simulate API call with mock data
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay
        setAuditLogs(getMockHistoryData());
      }
    } catch (error) {
      handleApiError(error);
      // On error, set empty array if using custom function, otherwise use mock data
      if (fetchAuditLogsFunction) {
        setAuditLogs([]);
      } else {
        setAuditLogs(getMockHistoryData());
      }
    } finally {
      setIsLoadingAuditLogs(false);
    }
  }, [invoiceId, fetchAuditLogsFunction, propAuditLogs, onRefresh]);

  // Fetch audit logs when modal opens (only if not provided as props)
  useEffect(() => {
    if (open && invoiceId && propAuditLogs === undefined) {
      fetchInvoiceHistory();
    }
  }, [open, invoiceId, fetchInvoiceHistory, propAuditLogs]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E8E9E9',
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontFamily: 'Inter',
            fontWeight: 600,
            color: '#192126',
          }}
        >
          Invoice Tracking/History
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={fetchInvoiceHistory}
            disabled={isLoadingAuditLogs}
            size="small"
            sx={{
              border: '1px solid #E8E9E9',
              borderRadius: '4px',
              padding: '6px',
              '&:hover': {
                backgroundColor: '#F8F9FA',
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: '16px', color: '#667085' }} />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#667085',
              '&:hover': {
                backgroundColor: '#F8F9FA',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {displayLoadingAuditLogs ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#BD1D23' }} />
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#667085',
              }}
            >
              Loading history...
            </Typography>
          </Box>
        ) : displayAuditLogs.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              flexDirection: 'column',
              gap: 2,
              color: '#667085',
            }}
          >
            <HistoryOutlinedIcon sx={{ fontSize: 48, color: '#D0D5DD' }} />
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#667085',
              }}
            >
              No history available
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              paddingTop: '16px',
              '& > div:first-child > div:first-child': {
                marginTop: '16px',
              },
            }}
          >
            <ActivityTimeline
              data={
                propAuditLogs !== undefined
                  ? displayAuditLogs
                  : transformAuditLogsForTimeline(displayAuditLogs)
              }
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceHistoryModal;
